// Auth predicate for the Mapbox transformRequest hook. Extracted from
// useMapLifecycle's map creation so it can be unit tested in isolation.
import { apiRequestHeadersFor } from "api/apiRequestHeaders";
import { DISPLAY_UNIT_HEADER } from "api/displayUnitHeader";

import { absolutizeServiceRoot } from "components/map/utils/absolutizeServiceRoot";

export interface MapAuthEndpoints {
  mapServiceEndpoint?: string;
  geoMapServiceEndpoint?: string;
  shapetileServiceEndpoint?: string;
  mapRsServiceEndpoint?: string;
}

/**
 * Whether `url` starts with `root` (as configured, or as absolutizeServiceRoot
 * renders it for tile templates) followed by `boundary`.
 */
const startsWithRoot = (url: string, root: Maybe<string>, boundary = ""): boolean =>
  !!root &&
  [root, absolutizeServiceRoot(root)].some((form) =>
    url.startsWith(`${form}${boundary}`)
  );

/**
 * Decides whether a tile/style request URL should carry the end-user Bearer
 * token. True for the node map service (which also serves the timeline tiles),
 * geo map service, shapetile service, and the map-rs service (org-scoped
 * rig/frac tiles).
 */
export function shouldAttachMapAuth(url: string, endpoints: MapAuthEndpoints): boolean {
  const {
    mapServiceEndpoint,
    geoMapServiceEndpoint,
    shapetileServiceEndpoint,
    mapRsServiceEndpoint
  } = endpoints;
  return (
    startsWithRoot(url, mapServiceEndpoint) ||
    startsWithRoot(url, geoMapServiceEndpoint) ||
    startsWithRoot(url, shapetileServiceEndpoint) ||
    // Shapetile also keeps the looser includes() match: its tile urls come from
    // the server's TileJSON response, so their exact prefix isn't
    // client-controlled, and a strict startsWith has broken research before.
    (!!shapetileServiceEndpoint && url.includes(shapetileServiceEndpoint)) ||
    // Anchored on "/" so a look-alike prefix ("…v1evil.com") can't pass. Every
    // real tile url is `${endpoint}/rigfrac/...`.
    startsWithRoot(url, mapRsServiceEndpoint, "/")
  );
}

/** Headers for an authenticated tile request: the bearer, the display unit once known, and the API marker on the page's own origin. */
export const mapRequestHeaders = (
  url: string,
  token: string,
  displayUnit: Maybe<string>
): Record<string, string> => ({
  ...apiRequestHeadersFor(url),
  Authorization: `Bearer ${token}`,
  ...(displayUnit ? { [DISPLAY_UNIT_HEADER]: displayUnit } : {})
});
